import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { downloadFile, fileUrl, uploadImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Modal, Field, Input, Select, Empty, Badge } from "@/components/ui-kit";
import { Plus, Trash2, Pencil, Link2 as LinkIcon, Users, Copy, KeyRound, FileText, Sheet, Camera, Upload, X, IdCard, MapPin, Phone, Cake, Archive, Search, Wallet, UserCheck } from "lucide-react";
import { toast } from "sonner";

const empty = { first_name: "", last_name: "", category: "", birthdate: "", position: "", jersey_number: "", phone: "", email: "", address: "", parent_user_ids: [], functions: ["joueur"], membership_type: "affilie", group: "" };
const GROUP_META = { gr1: { label: "GR 1", color: "purple" }, gr2: { label: "GR 2", color: "blue" } };
const STATUS_FILTERS = [{ key: "active", label: "Tous (actifs)" }, { key: "season", label: "Saison 2026/2027" }, { key: "archived", label: "Archivés" }];

const MEMBERSHIP_TYPES = [
  { key: "affilie", label: "Affilié" },
  { key: "essai", label: "Essai (gratuit)" },
  { key: "stages", label: "Stages" },
  { key: "seance", label: "À la séance" },
];
const MTYPE_META = {
  affilie: { label: "Affilié", color: "primary" },
  essai: { label: "Essai", color: "yellow" },
  stages: { label: "Stages", color: "purple" },
  seance: { label: "À la séance", color: "blue" },
};

const FUNC_COLOR = { joueur: "primary", coach: "blue", benevole: "green", delegue: "purple", comite_parents: "yellow", administrateur: "red" };

const ADMIN_COLORS = { green: "#22c55e", orange: "#f59e0b", red: "#ef4444", blue: "#3b82f6" };
const ADMIN_COLOR_LABEL = { green: "Vert", orange: "Orange", red: "Rouge", blue: "Bleu" };

export default function Members() {
  const { user, can, hasRole } = useAuth();
  const navigate = useNavigate();
  const isParent = user?.role === "parent";
  const isAdmin = user?.role === "admin";
  const canJoin = hasRole("parent");
  const canManage = can("members_manage");
  const canImport = can("members_import");
  const canExport = can("data_export");
  const canAdd = canManage || isParent;
  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [parents, setParents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [uploading, setUploading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("active");
  const [mtypeFilter, setMtypeFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [colorFilter, setColorFilter] = useState("all");
  const [colorLabels, setColorLabels] = useState({ green: "Vert", orange: "Orange", red: "Rouge", blue: "Bleu" });
  const [legendOpen, setLegendOpen] = useState(false);
  const [legendForm, setLegendForm] = useState(null);
  const [search, setSearch] = useState("");
  const [fiche, setFiche] = useState(null);
  const [myPhone, setMyPhone] = useState("");

  const load = () => {
    api.get(`/members?status=${statusFilter}`).then((r) => setMembers(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    if (canManage) api.get("/users?role=parent").then((r) => setParents(r.data)).catch(() => {});
    if (isAdmin) api.get("/settings/member-color-labels").then((r) => setColorLabels(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [statusFilter]);

  const setColor = async (m, color) => {
    const next = m.admin_color === color ? null : color;
    try {
      await api.patch(`/members/${m.id}/color`, { color: next || "" });
      setMembers((list) => list.map((x) => (x.id === m.id ? { ...x, admin_color: next } : x)));
    } catch { toast.error("Erreur"); }
  };
  const saveLegend = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/settings/member-color-labels", legendForm);
      setColorLabels(data); setLegendOpen(false); toast.success("Légende enregistrée");
    } catch { toast.error("Erreur"); }
  };

  const toggleStatus = async (m, field, value) => {
    try {
      const { data } = await api.patch(`/members/${m.id}/status`, { [field]: value });
      toast.success("Statut mis à jour");
      setMembers((list) => list.map((x) => x.id === m.id ? { ...x, ...data } : x));
      setFiche((f) => f && f.id === m.id ? { ...f, ...data } : f);
      if ((field === "archived" && value && statusFilter !== "archived" && statusFilter !== "all") ||
          (field === "archived" && !value && statusFilter === "archived")) {
        load();
      }
    } catch { toast.error("Erreur"); }
  };

  const saveMyPhone = async () => {
    try { await api.put("/me/profile", { phone: myPhone }); toast.success("Téléphone enregistré"); load(); }
    catch { toast.error("Erreur"); }
  };

  const openNew = () => { setEditing(null); setForm({ ...empty, category: categories[0]?.name || "" }); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...empty, ...m, parent_user_ids: m.parent_user_ids || [], jersey_number: m.jersey_number ?? "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, functions: ["joueur"], jersey_number: form.jersey_number ? parseInt(form.jersey_number) : null };
    try {
      if (editing) await api.put(`/members/${editing.id}`, payload);
      else await api.post("/members", payload);
      toast.success(editing ? "Membre mis à jour" : "Membre ajouté");
      setOpen(false); load();
    } catch (err) { toast.error("Erreur lors de l'enregistrement"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce membre ?")) return;
    await api.delete(`/members/${id}`); toast.success("Membre supprimé"); load();
  };

  const convertToAffilie = async (m) => {
    if (!window.confirm(`Passer ${m.first_name} ${m.last_name} en Affilié officiel ?`)) return;
    try {
      await api.post(`/members/${m.id}/convert-to-affilie`, {});
      toast.success("Membre passé en Affilié");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const convertToEssai = async (m) => {
    if (!window.confirm(`Passer ${m.first_name} ${m.last_name} en Essai ?`)) return;
    try {
      await api.post(`/members/${m.id}/convert-to-essai`, {});
      toast.success("Membre passé en Essai");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const joinChild = async (e) => {
    e.preventDefault();
    try {
      await api.post("/members/join", { code: joinCode });
      toast.success("Enfant rattaché à votre compte");
      setJoinOpen(false); setJoinCode(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Code invalide"); }
  };

  const unlinkParent = async (mid, uid, name) => {
    if (!window.confirm(`Retirer ${name || "ce parent"} de cet enfant ?`)) return;
    try {
      await api.delete(`/members/${mid}/parents/${uid}`);
      toast.success("Parent retiré");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const doImport = async (e) => {
    e.preventDefault();
    setImporting(true);
    try {
      const { data } = await api.post("/members/import", { sheet_url: importUrl });
      toast.success(`${data.imported} membre(s) importé(s) · ${data.skipped} ignoré(s)`);
      if (data.errors?.length) toast.warning(`${data.errors.length} ligne(s) en erreur`);
      setImportOpen(false); setImportUrl(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import échoué"); }
    finally { setImporting(false); }
  };

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = await uploadImage(file);
      setForm((f) => ({ ...f, photo: path }));
      toast.success("Photo ajoutée");
    } catch (err) { toast.error(err.response?.data?.detail || "Échec de l'upload"); }
    finally { setUploading(false); }
  };

  const copyCode = async (code) => {
    try {
      if (!navigator.clipboard) throw new Error("no clipboard");
      await navigator.clipboard.writeText(code);
      toast.success(`Code copié : ${code}`);
      return;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = code;
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) { toast.success(`Code copié : ${code}`); return; }
        throw new Error("execCommand failed");
      } catch {
        window.prompt("Copiez le code de partage (Ctrl/Cmd + C) :", code);
      }
    }
  };

  const filtered = (filter === "all" ? members : members.filter((m) => m.category === filter || (m.functions || []).includes(filter)))
    .filter((m) => !search || `${m.first_name} ${m.last_name}`.toLowerCase().includes(search.toLowerCase()))
    .filter((m) => mtypeFilter === "all" || (m.membership_type || "affilie") === mtypeFilter)
    .filter((m) => groupFilter === "all" || (groupFilter === "none" ? !m.group : m.group === groupFilter))
    .filter((m) => !isAdmin || colorFilter === "all" || (colorFilter === "none" ? !m.admin_color : m.admin_color === colorFilter));
  const sortedCats = [...categories].sort((a, b) => b.name.localeCompare(a.name, undefined, { numeric: true }));

  const exportQS = () => {
    const p = new URLSearchParams();
    p.set("status", statusFilter);
    if (filter !== "all") p.set("cat", filter);
    if (search) p.set("q", search);
    if (mtypeFilter !== "all") p.set("mtype", mtypeFilter);
    if (groupFilter !== "all") p.set("group", groupFilter);
    return p.toString();
  };

  return (
    <div>
      <PageHeader title="Affiliés" subtitle={isParent ? "Vos enfants rattachés à votre compte." : "Effectif du club affilié par catégorie."}
        action={(canAdd || canJoin) && (
          <div className="flex gap-2 flex-wrap">
            {canExport && <Button data-testid="export-members-pdf" variant="outline" onClick={() => downloadFile(`/export/members.pdf?${exportQS()}`, "effectif.pdf").catch(() => toast.error("Export échoué"))}><FileText size={16} /> PDF</Button>}
            {canExport && <Button data-testid="export-members-xlsx" variant="outline" onClick={() => downloadFile(`/export/members.xlsx?${exportQS()}`, "effectif.xlsx").catch(() => toast.error("Export échoué"))}><Sheet size={16} /> Excel</Button>}
            {canImport && <Button data-testid="import-members-btn" variant="outline" onClick={() => setImportOpen(true)}><Upload size={16} /> Importer</Button>}
            {canJoin && <Button data-testid="join-child-btn" variant="outline" onClick={() => setJoinOpen(true)}><KeyRound size={16} /> Rejoindre (code)</Button>}
            {canAdd && <Button data-testid="add-member-btn" onClick={openNew}><Plus size={16} /> {isParent ? "Mon enfant" : "Ajouter"}</Button>}
          </div>
        )} />

      {canManage && (
        <div className="flex gap-2 flex-wrap mb-4" data-testid="member-status-filter">
          {STATUS_FILTERS.map((s) => (
            <FilterChip key={s.key} active={statusFilter === s.key} onClick={() => setStatusFilter(s.key)}>{s.label}</FilterChip>
          ))}
        </div>
      )}

      <div className="relative mb-4 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10" />
        <Input data-testid="member-search" placeholder="Rechercher un affilié par nom..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
      </div>

      {!isParent && (
        <div className="flex flex-col gap-2 mb-6">
          <div className="flex gap-2 flex-wrap" data-testid="member-mtype-filter">
            <FilterChip active={mtypeFilter === "all"} onClick={() => setMtypeFilter("all")}>Tous types</FilterChip>
            {MEMBERSHIP_TYPES.map((t) => (
              <FilterChip key={t.key} active={mtypeFilter === t.key} onClick={() => setMtypeFilter(t.key)}>{t.label}</FilterChip>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap" data-testid="member-group-filter">
            <FilterChip active={groupFilter === "all"} onClick={() => setGroupFilter("all")}>Tous groupes</FilterChip>
            <FilterChip active={groupFilter === "gr1"} onClick={() => setGroupFilter("gr1")}>GR 1</FilterChip>
            <FilterChip active={groupFilter === "gr2"} onClick={() => setGroupFilter("gr2")}>GR 2</FilterChip>
            <FilterChip active={groupFilter === "none"} onClick={() => setGroupFilter("none")}>Sans groupe</FilterChip>
          </div>
          <div className="flex gap-2 flex-wrap">
            <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>Tous</FilterChip>
            {sortedCats.map((c) => (
              <FilterChip key={c.id} active={filter === c.name} onClick={() => setFilter(c.name)}>{c.name}</FilterChip>
            ))}
          </div>
          {isAdmin && (
            <div className="flex gap-2 flex-wrap items-center pt-1" data-testid="member-color-filter">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mr-1">Repères</span>
              <FilterChip active={colorFilter === "all"} onClick={() => setColorFilter("all")}>Tous</FilterChip>
              {Object.keys(ADMIN_COLORS).map((c) => (
                <button key={c} data-testid={`color-filter-${c}`} onClick={() => setColorFilter(colorFilter === c ? "all" : c)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border transition-colors ${colorFilter === c ? "border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: ADMIN_COLORS[c] }} /> {colorLabels[c]}
                </button>
              ))}
              <FilterChip active={colorFilter === "none"} onClick={() => setColorFilter(colorFilter === "none" ? "all" : "none")}>Sans repère</FilterChip>
              <button data-testid="edit-legend-btn" onClick={() => { setLegendForm({ ...colorLabels }); setLegendOpen(true); }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold text-primary hover:bg-primary/10 transition-colors"><Pencil size={12} /> Légende</button>
            </div>
          )}
        </div>
      )}

      {filtered.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((m) => (
            <Card key={m.id} data-testid="member-card" className="hover:-translate-y-1 transition-transform duration-200"
              style={isAdmin && m.admin_color ? { borderLeft: `5px solid ${ADMIN_COLORS[m.admin_color]}` } : undefined}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {m.photo ? (
                    <img src={fileUrl(m.photo)} alt={m.first_name} data-testid="member-photo"
                      className="w-12 h-12 rounded-full object-cover border border-border" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center text-primary font-display font-black text-lg">
                      {m.first_name?.[0]}{m.last_name?.[0]}
                    </div>
                  )}
                  <div>
                    {canManage ? (
                      <button data-testid={`member-name-${m.id}`} onClick={() => navigate(`/cotisations?member=${m.id}`)}
                        title="Voir la fiche financière" className="font-bold leading-tight text-left hover:text-primary hover:underline transition-colors flex items-center gap-1">
                        {m.first_name} {m.last_name} <Wallet size={13} className="text-primary/70" />
                      </button>
                    ) : (
                      <p className="font-bold leading-tight">{m.first_name} {m.last_name}</p>
                    )}
                    <p className="text-xs text-muted-foreground">{m.position || "Joueur"} {m.jersey_number ? `· #${m.jersey_number}` : ""}</p>
                    {(m.parent_user_ids?.length > 0 || m.parent_names?.length > 0) && (
                      canManage ? (
                        <div className="flex flex-wrap items-center gap-1.5 mt-1" data-testid="member-parents">
                          <Users size={11} className="text-primary/80" />
                          {(m.parent_user_ids || []).map((pid, i) => (
                            <span key={pid} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-secondary text-xs">
                              <span className="text-primary/90">{m.parent_names?.[i] || "Parent"}</span>
                              <button type="button" data-testid={`unlink-parent-${m.id}-${pid}`}
                                onClick={() => unlinkParent(m.id, pid, m.parent_names?.[i])}
                                className="text-muted-foreground hover:text-destructive" title="Retirer ce parent">
                                <X size={12} />
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-primary/80 mt-0.5 flex items-center gap-1"><Users size={11} /> {m.parent_names.join(" & ")}</p>
                      )
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge>{m.category}</Badge>
                  <Badge color={(MTYPE_META[m.membership_type] || MTYPE_META.affilie).color} data-testid={`member-mtype-${m.id}`}>{(MTYPE_META[m.membership_type] || MTYPE_META.affilie).label}</Badge>
                  {GROUP_META[m.group] && <Badge color={GROUP_META[m.group].color} data-testid={`member-group-${m.id}`}>{GROUP_META[m.group].label}</Badge>}
                  {m.archived && <Badge color="gray" data-testid={`archived-badge-${m.id}`}>Archivé</Badge>}
                </div>
              </div>

              {isAdmin && (
                <div className="flex items-center gap-2 mt-3" data-testid={`member-color-picker-${m.id}`}>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Repère</span>
                  <div className="flex items-center gap-1.5">
                    {Object.keys(ADMIN_COLORS).map((c) => (
                      <button key={c} type="button" data-testid={`member-color-${m.id}-${c}`} onClick={() => setColor(m, c)}
                        title={colorLabels[c]} aria-label={colorLabels[c]}
                        className={`w-5 h-5 rounded-full transition-transform hover:scale-110 ${m.admin_color === c ? "ring-2 ring-offset-2 ring-offset-card ring-foreground" : ""}`}
                        style={{ backgroundColor: ADMIN_COLORS[c] }} />
                    ))}
                    {m.admin_color && (
                      <button type="button" data-testid={`member-color-clear-${m.id}`} onClick={() => setColor(m, m.admin_color)}
                        title="Retirer la couleur" className="ml-0.5 text-muted-foreground hover:text-destructive"><X size={14} /></button>
                    )}
                  </div>
                  {m.admin_color && <span className="text-xs font-semibold" style={{ color: ADMIN_COLORS[m.admin_color] }} data-testid={`member-color-label-${m.id}`}>{colorLabels[m.admin_color]}</span>}
                </div>
              )}

              {m.share_code && (canManage || (m.parent_user_ids || []).includes(user?.id)) && (
                <button data-testid="share-code-btn" onClick={() => copyCode(m.share_code)}
                  className="mt-3 w-full flex items-center justify-between px-3 py-2 rounded-lg bg-secondary text-xs hover:bg-secondary/70 transition-colors">
                  <span className="text-muted-foreground">Code de partage co-parent</span>
                  <span className="font-mono font-bold text-primary flex items-center gap-1">{m.share_code} <Copy size={12} /></span>
                </button>
              )}

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                {user?.role !== "coach" ? (
                  (m.membership_type || "affilie") === "affilie" ? (
                    <div className="flex flex-col gap-0.5" data-testid={`cotisation-status-${m.id}`}>
                      <Badge color={m.cotisation_status === "paid" ? "green" : "yellow"}>
                        {m.cotisation_status === "paid"
                          ? "En ordre de cotisation"
                          : `À régler : ${m.cotisation_remaining ?? 0} €`}
                      </Badge>
                      {m.cotisation_status !== "paid" && m.cotisation_paid_amount > 0 && (
                        <span className="text-[11px] text-muted-foreground">Payé {m.cotisation_paid_amount} € / {m.cotisation_total} €</span>
                      )}
                    </div>
                  ) : (
                    <Badge color="blue" data-testid={`cotisation-status-${m.id}`}>
                      {m.membership_type === "essai" ? "Essai · gratuit" : m.membership_type === "seance" ? "À la séance" : "Stages"}
                    </Badge>
                  )
                ) : <span />}
                <div className="flex gap-1 flex-wrap items-center justify-end">
                  {canManage && (m.membership_type || "affilie") === "stages" && (
                    <button data-testid={`convert-essai-btn-${m.id}`} onClick={() => convertToEssai(m)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-yellow-500/15 text-yellow-500 text-xs font-bold hover:bg-yellow-500/25 transition-colors" title="Passer en Essai">
                      <UserCheck size={14} /> Passer en Essai
                    </button>
                  )}
                  {canManage && ((m.membership_type || "affilie") === "essai" || (m.membership_type || "affilie") === "stages") && (
                    <button data-testid={`convert-affilie-btn-${m.id}`} onClick={() => convertToAffilie(m)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary/15 text-primary text-xs font-bold hover:bg-primary/25 transition-colors" title="Passer en Affilié officiel">
                      <UserCheck size={14} /> Passer en Affilié
                    </button>
                  )}
                  <button data-testid={`member-fiche-btn-${m.id}`} onClick={() => { setFiche(m); setMyPhone((m.parent_contacts?.find((c) => c.id === user?.id) || {}).phone || ""); }} className="p-2 text-muted-foreground hover:text-primary" title="Fiche signalétique"><IdCard size={15} /></button>
                  {(canManage || (isParent && (m.parent_user_ids || []).includes(user?.id))) && (
                    <button data-testid="edit-member-btn" onClick={() => openEdit(m)} className="p-2 text-muted-foreground hover:text-primary"><Pencil size={15} /></button>
                  )}
                  {canManage && <button data-testid="delete-member-btn" onClick={() => remove(m.id)} className="p-2 text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : <Empty text={isParent ? "Ajoutez votre enfant ou rejoignez-en un avec un code de partage." : "Aucun membre pour l'instant."} />}

      <Modal open={legendOpen} onClose={() => setLegendOpen(false)} title="Légende des repères couleur">
        {legendForm && (
          <form onSubmit={saveLegend}>
            <p className="text-xs text-muted-foreground mb-3">Nommez chaque couleur pour vous y retrouver (ex. Rouge = pas prêt match). Visible par les administrateurs uniquement.</p>
            {Object.keys(ADMIN_COLORS).map((c) => (
              <div key={c} className="flex items-center gap-3 mb-3">
                <span className="w-6 h-6 rounded-full shrink-0" style={{ backgroundColor: ADMIN_COLORS[c] }} />
                <Input data-testid={`legend-input-${c}`} value={legendForm[c]} onChange={(e) => setLegendForm({ ...legendForm, [c]: e.target.value })} placeholder={ADMIN_COLOR_LABEL[c]} />
              </div>
            ))}
            <Button data-testid="save-legend-btn" className="w-full mt-1">Enregistrer la légende</Button>
          </form>
        )}
      </Modal>


      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Modifier le joueur" : (isParent ? "Ajouter mon enfant" : "Nouveau membre")}>
        <form onSubmit={save} data-testid="member-form">
          <div className="flex items-center gap-4 mb-4">
            {form.photo ? (
              <img src={fileUrl(form.photo)} alt="aperçu" className="w-16 h-16 rounded-full object-cover border border-border" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center text-muted-foreground">
                <Camera size={22} />
              </div>
            )}
            <label className="cursor-pointer">
              <span className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border text-sm font-bold hover:bg-secondary transition-colors">
                <Camera size={15} /> {uploading ? "Envoi..." : (form.photo ? "Changer la photo" : "Ajouter une photo")}
              </span>
              <input data-testid="member-photo-input" type="file" accept="image/*" className="hidden" onChange={onPhotoChange} disabled={uploading} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Prénom"><Input data-testid="member-firstname" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Field>
            <Field label="Nom"><Input data-testid="member-lastname" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></Field>
          </div>
          <Field label="Catégorie (année de naissance)">
            <Select data-testid="member-category" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              <option value="">Sélectionner...</option>
              {sortedCats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </Select>
          </Field>
          {canManage && (
            <Field label="Type d'adhésion">
              <Select data-testid="member-membership-type" value={form.membership_type || "affilie"}
                onChange={(e) => setForm({ ...form, membership_type: e.target.value })}>
                {MEMBERSHIP_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Affilié : cotisation annuelle. Essai : gratuit, sans cotisation. À la séance : paiement à chaque présence.
              </p>
            </Field>
          )}
          {canManage && (
            <Field label="Groupe">
              <Select data-testid="member-group" value={form.group || ""}
                onChange={(e) => setForm({ ...form, group: e.target.value })}>
                <option value="">Automatique (selon année de naissance)</option>
                <option value="gr1">GR 1</option>
                <option value="gr2">GR 2</option>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Par défaut : GR 1 = 2020-2023, GR 2 = 2015-2019. Choisissez pour forcer un groupe.
              </p>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Poste"><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="Gardien, Pivot..." /></Field>
            <Field label="Numéro"><Input type="number" value={form.jersey_number} onChange={(e) => setForm({ ...form, jersey_number: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Naissance"><Input type="date" value={form.birthdate} onChange={(e) => {
              const bd = e.target.value;
              const yr = bd ? bd.slice(0, 4) : "";
              const match = categories.find((c) => c.name === yr);
              setForm((f) => ({ ...f, birthdate: bd, category: match ? match.name : f.category }));
            }} /></Field>
            <Field label="Téléphone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          </div>
          <Field label="Email (pour les rappels)"><Input data-testid="member-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="parent@exemple.com" /></Field>
          <Field label="Adresse"><Input data-testid="member-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Rue, n°, code postal, ville" /></Field>
          {canManage && (
            <Field label="Compte parent principal">
              <Select data-testid="member-parent" value={form.parent_user_ids?.[0] || ""} onChange={(e) => setForm({ ...form, parent_user_ids: e.target.value ? [e.target.value] : [] })}>
                <option value="">Aucun</option>
                {parents.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.email})</option>)}
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Le co-parent (parents séparés) pourra se rattacher avec le code de partage affiché sur la fiche.</p>
            </Field>
          )}
          {isParent && !editing && (
            <p className="text-xs text-muted-foreground mb-3">Votre enfant sera automatiquement rattaché à votre compte. Un code de partage sera généré pour que l'autre parent puisse aussi le suivre.</p>
          )}
          {user?.role === "admin" && (
            <button type="button" data-testid="member-pack-manager-toggle"
              onClick={() => setForm((f) => ({ ...f, pack_manager: !f.pack_manager }))}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border text-sm font-bold transition-colors mb-2 ${form.pack_manager ? "bg-primary/15 border-primary/50 text-primary" : "bg-secondary border-border text-muted-foreground hover:text-foreground"}`}>
              <span>Responsable pack vêtements</span>
              <span>{form.pack_manager ? "OUI" : "NON"}</span>
            </button>
          )}
          <Button data-testid="save-member-btn" className="w-full mt-2">Enregistrer</Button>
        </form>
      </Modal>

      <Modal open={!!fiche} onClose={() => setFiche(null)} title="Fiche signalétique">
        {fiche && (
          <div className="space-y-4" data-testid="member-fiche">
            <div className="flex items-center gap-3">
              {fiche.photo ? <img src={fileUrl(fiche.photo)} alt="" className="w-14 h-14 rounded-full object-cover border border-border" />
                : <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center text-primary font-display font-black text-xl">{fiche.first_name?.[0]}{fiche.last_name?.[0]}</div>}
              <div>
                <p className="font-display text-xl font-black">{fiche.first_name} {fiche.last_name}</p>
                <p className="text-sm text-muted-foreground">{fiche.category}{fiche.position ? ` · ${fiche.position}` : ""}{fiche.jersey_number ? ` · #${fiche.jersey_number}` : ""}</p>
                <div className="mt-1.5"><Badge color={(MTYPE_META[fiche.membership_type] || MTYPE_META.affilie).color} data-testid="fiche-mtype-badge">{(MTYPE_META[fiche.membership_type] || MTYPE_META.affilie).label}</Badge>
                {GROUP_META[fiche.group] && <span className="ml-1.5"><Badge color={GROUP_META[fiche.group].color} data-testid="fiche-group-badge">{GROUP_META[fiche.group].label}</Badge></span>}</div>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><Cake size={15} className="text-primary shrink-0" /><span className="text-muted-foreground">Naissance :</span> <span className="font-medium">{fiche.birthdate || "—"}</span></div>
              <div className="flex items-start gap-2"><MapPin size={15} className="text-primary shrink-0 mt-0.5" /><span className="text-muted-foreground">Adresse :</span> <span className="font-medium">{fiche.address || "—"}</span></div>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Comptes parents liés</p>
              {(fiche.parent_contacts || []).length ? (
                <div className="space-y-2" data-testid="fiche-parents">
                  {fiche.parent_contacts.map((p) => (
                    <div key={p.id} className="p-3 rounded-lg bg-secondary/50">
                      <p className="font-semibold text-sm">{p.name || "Parent"}</p>
                      {p.email && <p className="text-xs text-muted-foreground">{p.email}</p>}
                      <p className="text-xs flex items-center gap-1 mt-0.5"><Phone size={11} className="text-primary" /> {p.phone || <span className="text-muted-foreground italic">non renseigné</span>}</p>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Aucun compte parent lié.</p>}
            </div>

            {canManage && (
              <div className="space-y-2 pt-2 border-t border-border" data-testid="fiche-admin-status">
                <label className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 cursor-pointer">
                  <input type="checkbox" data-testid="fiche-affiliated-toggle" className="w-4 h-4 accent-[color:var(--primary)]"
                    checked={!!fiche.affiliated_current} onChange={(e) => toggleStatus(fiche, "affiliated_current", e.target.checked)} />
                  <span className="text-sm font-semibold">Affilié saison 2026/2027</span>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 cursor-pointer">
                  <input type="checkbox" data-testid="fiche-archived-toggle" className="w-4 h-4 accent-[color:var(--primary)]"
                    checked={!!fiche.archived} onChange={(e) => toggleStatus(fiche, "archived", e.target.checked)} />
                  <span className="text-sm font-semibold flex items-center gap-1"><Archive size={14} /> Archivé (plus actif)</span>
                </label>
              </div>
            )}

            {canManage && (
              <div className="space-y-2 pt-2 border-t border-border" data-testid="fiche-admin-docs">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Documents administratifs</p>
                {[["doc_aptitude", "Déclaration aptitudes physiques en ordre"], ["doc_droit_image", "Déclaration droit à l'image"], ["doc_roi", "ROI (règlement d'ordre intérieur)"]].map(([k, lbl]) => (
                  <label key={k} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 cursor-pointer">
                    <input type="checkbox" data-testid={`fiche-${k}-toggle`} className="w-4 h-4 accent-[color:var(--primary)]"
                      checked={!!fiche[k]} onChange={(e) => toggleStatus(fiche, k, e.target.checked)} />
                    <span className="text-sm font-semibold">{lbl}</span>
                  </label>
                ))}
              </div>
            )}

            {isParent && (
              <div className="space-y-2 pt-2 border-t border-border" data-testid="fiche-docs-readonly">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Documents administratifs</p>
                {[["doc_aptitude", "Aptitudes physiques"], ["doc_droit_image", "Droit à l'image"], ["doc_roi", "ROI"]].map(([k, lbl]) => (
                  <div key={k} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 text-sm">
                    <span className="font-medium">{lbl}</span>
                    <Badge color={fiche[k] ? "green" : "red"} data-testid={`fiche-${k}-status`}>{fiche[k] ? "En ordre" : "Non"}</Badge>
                  </div>
                ))}
              </div>
            )}

            {isParent && (fiche.parent_user_ids || []).includes(user?.id) && (
              <div className="pt-2 border-t border-border" data-testid="fiche-my-phone">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Mon téléphone (visible sur la fiche)</p>
                <div className="flex gap-2">
                  <Input data-testid="my-phone-input" value={myPhone} onChange={(e) => setMyPhone(e.target.value)} placeholder="+32 ..." />
                  <Button data-testid="save-my-phone-btn" type="button" onClick={saveMyPhone}>Enregistrer</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal open={joinOpen} onClose={() => setJoinOpen(false)} title="Rejoindre un enfant">
        <form onSubmit={joinChild}>
          <p className="text-sm text-muted-foreground mb-4">Saisissez le code de partage fourni par l'autre parent ou le club pour suivre le même enfant.</p>
          <Field label="Code de partage">
            <Input data-testid="join-code-input" required value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="Ex : A1B2C3D4" className="font-mono uppercase" />
          </Field>
          <Button data-testid="join-submit-btn" className="w-full mt-2"><LinkIcon size={16} /> Rattacher</Button>
        </form>
      </Modal>

      <Modal open={importOpen} onClose={() => setImportOpen(false)} title="Importer depuis Google Sheet">
        <form onSubmit={doImport}>
          <p className="text-sm text-muted-foreground mb-4">
            Collez le lien d'une feuille Google Sheets <b>publique</b> (Partage → « Toute personne disposant du lien peut consulter »).
          </p>
          <Field label="Lien Google Sheet">
            <Input data-testid="import-url-input" required type="url" value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/.../edit#gid=0" />
          </Field>
          <div className="rounded-lg bg-secondary/60 border border-border p-3 mb-4">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Colonnes reconnues</p>
            <p className="text-xs text-muted-foreground">Prénom, Nom, Catégorie (année), Date de naissance, Poste, Numéro, Téléphone, Email. La 1ʳᵉ ligne doit contenir les en-têtes.</p>
            <button type="button" data-testid="download-members-template-btn"
              onClick={() => downloadFile("/members/import-template.csv", "modele_import_membres.csv").catch(() => toast.error("Téléchargement échoué"))}
              className="mt-2 text-xs font-bold text-primary hover:underline flex items-center gap-1">
              <FileText size={13} /> Télécharger le modèle (CSV)
            </button>
          </div>
          <Button data-testid="import-submit-btn" className="w-full mt-2" disabled={importing}>
            <Upload size={16} /> {importing ? "Import en cours..." : "Importer les membres"}
          </Button>
        </form>
      </Modal>
    </div>
  );
}

const FilterChip = ({ active, children, ...props }) => (
  <button {...props} className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{children}</button>
);
